import { FetchPolicy, WatchQueryFetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';
import { client } from '..';
import { useQuery } from 'react-apollo';

export interface ApplicationSettingsData {
    settings: Settings;
}
export interface Settings {
    id:           number;
    applications: Applications;
}

export interface Applications {
    id:                        number;
    settingNames:              SettingNames;
    mobileSettings:            MobileSettings;
    dataPublisherSettings:     DataPublisherSettings;
    documentPublisherSettings: DocumentPublisherSettings;
    taskPublisherSettings:     TaskPublisherSettings;
    licencing:                 Licencing;
    staffBudgetSettings:       StaffBudgetSettings;
    futureTimeSynchronization: FutureTimeSynchronization;
    clientNotesSettings:       ClientNotesSettings;
    labelSettings:             LabelSettings;
    systemOptions:             SystemOptions;
    ratesSyncSettings:         RatesSyncSettings;
    chargeRateSyncSettings:    ChargeRateSyncSettings;
}

export interface SystemOptions {
    config: SystemOptionsConfig;
}

export interface LabelSettings {
    config: LabelSettingsConfig;
}

export interface ClientNotesSettings {
    config: ClientNotesSettingsConfig;
}

export interface ClientNotesSettingsConfig {
    enableClientNotesSync: boolean;
}

export interface StaffBudgetSettings {
    config: StaffBudgetSettingsConfig;
}

export interface RatesSyncSettings {
    config: RatesSyncSettingsConfig;
}

export interface ChargeRateSyncSettings {
    config: ChargeRateSyncSettingsConfig;
}

export interface StaffBudgetSettingsConfig {
    enableBudgetSync: boolean;
}

export interface RatesSyncSettingsConfig {
    enableRatesSync: boolean;
}

export interface ChargeRateSyncSettingsConfig {
    enableChargeRateSync: boolean;
}

export interface DataPublisherSettings {
    config: DataPublisherSettingsConfig;
}

export interface DataPublisherSettingsConfig {
    publishTimeline:   boolean;
    publishFinancials: boolean;
    timeOnly:          boolean;
}

export interface DocumentPublisherSettings {
    config: DocumentPublisherSettingsConfig;
}

export interface TaskPublisherSettings {
    config: TaskPublisherSettingsConfig;

}

export interface FutureTimeSynchronization {
    enableFutureTimeSynchronization: boolean;
}

export interface DocumentPublisherSettingsConfig {
    publishDocuments: boolean;
    publishDocumentFolders: boolean;
}

export interface TaskPublisherSettingsConfig {
    publishTasks: boolean;
    publishSuperDiaryTasks: boolean;
}

export interface MobileSettings {
    config: MobileSettingsConfig;
}

export interface MobileSettingsConfig {
    showTimerButton:     boolean;
    showMatterSelection: boolean;
    showPhoneCallButton: boolean;
}

export interface Licencing {
    config: LicencingConfig;
}

export interface LicencingConfig {
    internalRegisteredUsers: number;
}
export interface SettingNames {
    recordCount: number;
    settingName: SettingName[];
}

export interface SettingName {
    id:           number;
    title:        string;
    description:  string;
    guidID:       string;
    internalName: string;
}

export interface LabelSettingsConfig {
    personActingLabel: string | null;
    personResponsibleLabel: string | null;
    matterAssistingResponsibilityLabel: string | null;
    matterIntroducingResponsibilityLabel: string | null;
    matterWorkingResponsibilityMapping: string | null;
    matterOversightResponsibilityMapping: string | null;
    matterAssistingResponsibilityMapping: string | null;
    matterIntroducerResponsibilityMapping: string | null;
}

export interface SystemOptionsConfig {
    emailSizeLimit: number | null;
    includeCreditsInArTotals: boolean;
    matterOpeningDateNaming: string | null;
    matterClosingDateNaming: string | null;
    showLastFinancialSyncUpdateDate: boolean;
    lastFinancialSyncUpdateDatetime: string | null;
}

export function retrieveApplicationSettingsData(
    refreshData: boolean,
    onSuccess: (data: ApplicationSettingsData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void)
    : void {
        var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';

        client.query({
            query: APPLICATION_SETTINGS_QUERY,
            variables: {},
            fetchPolicy: fetchPolicy
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any; }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any ) => { onError(reason); });
}

export const APPLICATION_SETTINGS_QUERY = gql`
    query applicationSettings {
        settings {
            id
            applications {
                id
                settingNames {
                    recordCount,
                    settingName {
                        id,
                        title,
                        description,
                        guidID,
                        internalName
                    }        
                }
                mobileSettings {
                    config {
                        showTimerButton
                        showMatterSelection
                        showPhoneCallButton
                    }
                }
                dataPublisherSettings {
                    config {
                        publishTimeline
                        publishFinancials
                        timeOnly
                    }
                }
                documentPublisherSettings {
                    config {
                        publishDocuments
                        publishDocumentFolders
                    }
                }
                taskPublisherSettings {
                    config {
                        publishTasks
                        publishSuperDiaryTasks
                    }
                }
                licencing {
                    config {
                        internalRegisteredUsers
                    }
                }
                clientNotesSettings {
                    config {
                        enableClientNotesSync
                    }
                }
                staffBudgetSettings {
                    config {
                        enableBudgetSync
                    }
                }
                ratesSyncSettings{
                    config{
                        enableRatesSync
                    }
                }
                chargeRateSyncSettings{
                    config{
                        enableChargeRateSync
                    }
                }
                futureTimeSynchronization {
                    enableFutureTimeSynchronization
                }
                labelSettings {
                    config {
                        personActingLabel
                        personResponsibleLabel
                        matterAssistingResponsibilityLabel
                        matterIntroducingResponsibilityLabel
                        matterWorkingResponsibilityMapping
                        matterOversightResponsibilityMapping
                        matterAssistingResponsibilityMapping
                        matterIntroducerResponsibilityMapping
                    }
                }   
                systemOptions {
                    config {
                        emailSizeLimit
                        includeCreditsInArTotals
                        matterOpeningDateNaming
                        matterClosingDateNaming
                        showLastFinancialSyncUpdateDate
                        lastFinancialSyncUpdateDatetime
                    }
                }
            }
        }
    }
`;

export const useApplicationSettings = (fetchPolicy?: WatchQueryFetchPolicy) => {

    const { data, error, loading } = useQuery<ApplicationSettingsData>(APPLICATION_SETTINGS_QUERY, {
        fetchPolicy: fetchPolicy ?? 'cache-and-network'
    });

    return {
        data,
        error,
        loading
    };

};
